import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Heading,
  HStack,
  ListItem,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList
} from '@chakra-ui/react'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/20/solid'
import { v4 as uuidv4 } from 'uuid'

import axios from 'lib/axios'
import { getPayOrQuitApiError } from './payOrQuitMessages'

type PostingCandidate = {
  rental_id: string
  address: string
  resident_names: string[]
  amount_due: string
  amount_due_formatted: string
}

type SkippedRental = {
  rental_id: string
  address: string
  reasons: string[]
  show_in_preview?: boolean
  notice_sent_at?: string
  summary?: string
}

type PostingPreview = {
  generation_date: string
  time_zone: string
  candidates: PostingCandidate[]
  excluded: SkippedRental[]
  template_version: string
}

type PostingBatch = {
  id: string
  generation_date: string
  time_zone: string
  status: 'queued' | 'processing' | 'ready' | 'failed_retryable' | 'failed_terminal'
  included_count: number
  skipped: SkippedRental[]
  notices: PostingCandidate[]
  error_message: string | null
}

type PostingSelection = { state: string; month: number; year: number }

const batchIsPending = (batch: PostingBatch | null) =>
  batch && ['queued', 'processing', 'failed_retryable'].includes(batch.status)

const ExcludedHomes = ({ homes, heading }: { homes: SkippedRental[]; heading: string }) =>
  homes.length ? (
    <Box as='details' borderWidth='1px' borderRadius='md' padding={3}>
      <Box as='summary' cursor='pointer' fontWeight='medium'>
        {heading} ({homes.length})
      </Box>
      <Box
        role='region'
        aria-label={heading}
        tabIndex={0}
        maxHeight='min(35vh, 320px)'
        overflowY='auto'
        marginTop={2}
        _focusVisible={{ outline: '2px solid', outlineColor: 'teal.500', outlineOffset: '2px' }}
      >
        <UnorderedList spacing={2} fontSize='sm' paddingRight={3}>
          {homes.map((home) => (
            <ListItem key={home.rental_id}>
              <Text as='span' fontWeight='medium'>
                {home.address}
              </Text>
              {': '}
              {home.notice_sent_at && (
                <>
                  Notice sent{' '}
                  {new Intl.DateTimeFormat('en-US', {
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'America/New_York'
                  }).format(new Date(home.notice_sent_at))}
                  {'. '}
                </>
              )}
              {home.summary ?? home.reasons.join('; ')}
            </ListItem>
          ))}
        </UnorderedList>
      </Box>
    </Box>
  ) : null

const PayOrQuitPostingPanel = ({ state, year, month }: PostingSelection) => {
  const [preview, setPreview] = useState<PostingPreview | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [batchError, setBatchError] = useState<string | null>(null)
  const [batch, setBatch] = useState<PostingBatch | null>(null)
  const [batchId, setBatchId] = useState<string | null>(null)
  const [downloadStarted, setDownloadStarted] = useState(false)
  const generatingRef = useRef(false)
  const mountedRef = useRef(true)
  const hasPreviewRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    let current = true
    setIsLoading(true)
    setError(null)
    axios
      .get<PostingPreview>('/admin/pay-or-quit/posting/preview', { params: { state, year, month } })
      .then(({ data }) => {
        if (!current) return
        setPreview(data)
        const availableIds = data.candidates.map((candidate) => candidate.rental_id)
        const hadPreview = hasPreviewRef.current
        setSelectedIds((ids) =>
          hadPreview ? ids.filter((id) => availableIds.includes(id)) : availableIds
        )
        hasPreviewRef.current = true
      })
      .catch((requestError) => {
        if (!current) return
        setPreview(null)
        setSelectedIds([])
        setError(getPayOrQuitApiError(requestError))
      })
      .finally(() => {
        if (current) setIsLoading(false)
      })
    return () => {
      current = false
    }
  }, [state, year, month, reloadKey])

  useEffect(() => {
    if (!batchId) return
    let current = true
    let timer: ReturnType<typeof setTimeout>
    const finish = () => {
      generatingRef.current = false
      setIsGenerating(false)
      setIsDownloading(false)
      setBatchId(null)
    }
    const checkBatch = async () => {
      let nextBatch: PostingBatch
      try {
        const { data } = await axios.get<{ batch: PostingBatch }>(
          `/admin/pay-or-quit/posting/batches/${batchId}`
        )
        if (!current) return
        nextBatch = data.batch
        setBatch(nextBatch)
        setBatchError(null)
      } catch (requestError) {
        if (!current) return
        setBatchError(getPayOrQuitApiError(requestError))
        timer = setTimeout(checkBatch, 5000)
        return
      }
      if (batchIsPending(nextBatch)) {
        timer = setTimeout(checkBatch, 3000)
      } else if (nextBatch.status === 'ready') {
        setIsDownloading(true)
        try {
          const { data } = await axios.post<{ url: string }>(
            `/admin/pay-or-quit/posting/batches/${batchId}/download`
          )
          if (!current) return
          window.location.assign(data.url)
          setDownloadStarted(true)
        } catch (requestError) {
          if (current) setError(getPayOrQuitApiError(requestError))
        } finally {
          if (current) finish()
        }
      } else {
        setError(nextBatch.error_message || 'We could not create your notices. Please try again.')
        finish()
      }
    }
    checkBatch()
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [batchId])

  const downloadNotices = async () => {
    if (generatingRef.current || !selectedIds.length) return
    generatingRef.current = true
    setIsGenerating(true)
    setError(null)
    setBatchError(null)
    setBatch(null)
    setDownloadStarted(false)
    try {
      const { data } = await axios.post<{ batch: PostingBatch }>(
        '/admin/pay-or-quit/posting/batches',
        { state, year, month, idempotency_key: uuidv4(), rental_ids: [...selectedIds].sort() }
      )
      if (!mountedRef.current) return
      setBatch(data.batch)
      setBatchId(data.batch.id)
    } catch (requestError) {
      generatingRef.current = false
      if (mountedRef.current) {
        setError(getPayOrQuitApiError(requestError))
        setIsGenerating(false)
      }
    }
  }

  const selectionDisabled = isLoading || isGenerating
  const allSelected = Boolean(
    preview?.candidates.length && selectedIds.length === preview.candidates.length
  )

  return (
    <Stack spacing={4}>
      {error && (
        <Alert status='error'>
          <AlertIcon />
          {error}
        </Alert>
      )}
      <HStack justifyContent='space-between' flexWrap='wrap' gap={2}>
        <Heading size='md'>Eligible homes</Heading>
        <Button
          size='sm'
          variant='outline'
          leftIcon={<ArrowPathIcon className='h-4 w-4' />}
          onClick={() => setReloadKey((key) => key + 1)}
          isDisabled={isGenerating}
          isLoading={isLoading}
        >
          Refresh balances
        </Button>
      </HStack>
      {isLoading ? (
        <HStack role='status'>
          <Spinner size='sm' />
          <Text>Checking current balances…</Text>
        </HStack>
      ) : preview?.candidates.length ? (
        <>
          <Text fontSize='sm'>
            {selectedIds.length} of {preview.candidates.length} homes selected.
          </Text>
          <TableContainer
            role='region'
            aria-label='Eligible homes'
            tabIndex={0}
            borderWidth='1px'
            borderRadius='md'
            whiteSpace='normal'
            maxHeight='min(55vh, 520px)'
            overflowY='auto'
            _focusVisible={{ outline: '2px solid', outlineColor: 'teal.500', outlineOffset: '2px' }}
          >
            <Table size='sm'>
              <Thead sx={{ th: { position: 'sticky', top: 0, background: 'gray.50', zIndex: 1 } }}>
                <Tr>
                  <Th width='40px' verticalAlign='middle'>
                    <Checkbox
                      aria-label='Select all eligible homes'
                      display='flex'
                      marginBottom={0}
                      isChecked={allSelected}
                      isIndeterminate={selectedIds.length > 0 && !allSelected}
                      isDisabled={selectionDisabled}
                      onChange={(event) =>
                        setSelectedIds(
                          event.target.checked
                            ? preview.candidates.map((candidate) => candidate.rental_id)
                            : []
                        )
                      }
                    />
                  </Th>
                  <Th>Property</Th>
                  <Th>Residents</Th>
                  <Th isNumeric>Amount due</Th>
                </Tr>
              </Thead>
              <Tbody>
                {preview.candidates.map((candidate) => (
                  <Tr key={candidate.rental_id}>
                    <Td>
                      <Checkbox
                        aria-label={`Include ${candidate.address}`}
                        isChecked={selectedIds.includes(candidate.rental_id)}
                        isDisabled={selectionDisabled}
                        onChange={(event) =>
                          setSelectedIds((ids) =>
                            event.target.checked
                              ? [...ids, candidate.rental_id]
                              : ids.filter((id) => id !== candidate.rental_id)
                          )
                        }
                      />
                    </Td>
                    <Td>{candidate.address}</Td>
                    <Td>{candidate.resident_names.join(', ')}</Td>
                    <Td isNumeric>{candidate.amount_due_formatted}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </>
      ) : preview ? (
        <Alert status='info'>
          <AlertIcon />
          No homes currently qualify for this state and month.
        </Alert>
      ) : null}
      {Boolean(preview?.candidates.length) && (
        <Stack spacing={2} alignItems='flex-start'>
          <Button
            colorScheme='teal'
            onClick={downloadNotices}
            isDisabled={selectionDisabled || !selectedIds.length}
            isLoading={isGenerating}
            loadingText={isDownloading ? 'Starting download…' : 'Preparing notices…'}
            leftIcon={<ArrowDownTrayIcon className='h-4 w-4' />}
          >
            Download notices
          </Button>
          {!isGenerating && (
            <Text fontSize='sm' color='gray.600'>
              Preparing PDFs may take a minute or more.
            </Text>
          )}
          {isGenerating && batch?.status !== 'failed_retryable' && (
            <Text role='status' fontSize='sm'>
              {isDownloading
                ? 'Your PDFs are ready. Starting your download…'
                : 'Preparing your PDFs. This may take a minute or more—please keep this page open.'}
            </Text>
          )}
        </Stack>
      )}
      {preview && (
        <ExcludedHomes
          homes={preview.excluded.filter((home) => home.show_in_preview !== false)}
          heading='Homes not eligible'
        />
      )}
      {isGenerating && batch?.status === 'failed_retryable' && (
        <Text role='status' fontSize='sm'>
          A temporary problem delayed your notices. We are retrying automatically.
        </Text>
      )}
      {downloadStarted && <Text role='status'>Your download has started.</Text>}
      {!isGenerating && batch?.status === 'ready' && (
        <ExcludedHomes homes={batch.skipped} heading='Skipped during generation' />
      )}
      {batchError && (
        <Alert status='warning'>
          <AlertIcon />
          {batchError} We are still checking on your notices.
        </Alert>
      )}
    </Stack>
  )
}

export default PayOrQuitPostingPanel
